import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Link } from 'gatsby';
import styled, { css } from 'styled-components';
import { Button } from './Button.js';
import Scrollspy from 'react-scrollspy';
import theme from '../themes/theme.js';

import SubMenu from './reusable/SubMenu.js';
import { productsSubMenuItems, resourcesSubMenuItems } from '../helper/constant.js';
import useScrollNavbar from '../hooks/useScrollNavbar.js';
import { Menu } from '@mui/icons-material';
import ResponsiveNavbar from './ResponsiveNavbar.jsx';

const Container = styled.nav`
    background: ${({ bgColor }) => bgColor};
    width: 100%;
    position: fixed;
    top: 0;
    z-index: 10;
    transition: background-color 1s ease-in-out;
    box-shadow: 0px 0px 4px 0px #00000014;
`;

const Nav = styled.div`
    max-width: 1280px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 8px 24px;
    margin: 0 auto;

    @media screen and (max-width: 912px) {
        padding: 16px;
    }
`;

const Image = styled.img`
    width: 144px;
    height: auto;
`;

const NavLinks = styled.div`
    padding: 0;
    margin-left: 30px;

    @media screen and (max-width: 912px) {
        position: absolute;
        top: 50px;
        left: 0;
        background: rgba(255, 255, 255, 1);
        display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
        flex-direction: column;
        width: 100%;
        text-align: center;
        padding: 20px 0 24px;
        margin-left: 0;
    }
`;

const ScrollSpyDiv = styled(Scrollspy)`
    @media screen and (max-width: 912px) {
        display: flex;
        flex-direction: column;
    }
`;

const PageLink = styled(Link)`
    margin: 12px;
    font-size: 14px;
    font-weight: 400;
    text-decoration: none;
    color: ${({ color }) => color};
    padding-bottom: 8px;

    @media screen and (min-width: 912px) {
        &:hover {
            background: ${({ linkHoverIcon }) => `url(${linkHoverIcon}) no-repeat`};
            background-position: center bottom;
        }

        &.active {
            background: ${({ linkHoverIcon }) => `url(${linkHoverIcon}) no-repeat`};
            background-position: center bottom;
        }
    }
    ${({ mobileOnly }) =>
        mobileOnly &&
        css`
            @media screen and (min-width: 912px) {
                display: none;
            }
        `}

    ${({ dropdown }) =>
        dropdown &&
        css`
            position: relative;
        `}
`;

const SignInLink = styled(Button)`
    display: none;
    transition: all 0.2s;

    a {
        text-decoration: none;
        color: white;
    }

    &:hover {
        transform: scale(1.1);
    }

    @media screen and (max-width: 912px) {
        display: block;
        margin-bottom: 24px;
        margin: 0 auto;
    }
`;

const MenuBar = styled.div`
    display: none;

    @media screen and (max-width: 912px) {
        display: block;
        cursor: pointer;
        width: 24px;
        height: 24px;
    }
`;

const MenuIcon = styled(Menu)`
    width: 24px;
    height: 24px;
    color: ${({ color }) => (color === theme.colors.white ? color : '#171717')};
`;

const NavBtnGroup = styled.div`
    @media screen and (max-width: 912px) {
        display: none;
    }
`;

const LoginLink = styled.a`
    margin: 12px;
    font-weight: 400;
    font-size: 14px;
    line-height: 19px;
    text-decoration: none;
    color: #252963;
    padding-bottom: 5px;
    ${({ disable }) =>
        disable
            ? css`
                  display: none;
              `
            : css`
                  display: initial;
              `}

    @media screen and (max-width: 912px) {
        display: block;
    }
`;

const ContactUsCta = styled.a`
    display: inline-block;
    font-size: 16px;
    font-weight: 400;
    line-height: 18.96px;
    padding: 9.5px 16px;
    border-radius: 4px;
    cursor: pointer;
    text-decoration: none;
    border: 1px solid ${({ currentColor }) => currentColor};
    color: ${({ currentColor }) => currentColor};
    transition: all 0.2s;

    @media screen and (max-width: 912px) {
        display: none;
    }

    &:hover {
        transform: scale(1.1);
    }
`;

const GetStartedCta = styled.a`
    font-size: 16px;
    font-weight: 400;
    line-height: 18.96px;
    padding: 9.5px 16px;
    border-radius: 4px;
    cursor: pointer;
    text-decoration: none;
    background-color: ${({ currentColor }) => currentColor};
    color: ${({ currentColor }) =>
        currentColor === theme.colors.white ? theme.colors.navbarTextPrimary : theme.colors.white};
    transition: all 0.2s;
    margin-left: 16px;
    display: inline-block;
    border: 1px solid ${({ currentColor }) => currentColor};

    @media screen and (max-width: 912px) {
        display: none;
    }

    &:hover {
        transform: scale(1.1);
    }
`;

const ImageLink = styled(Link)``;

const NavWrapper = styled.div`
    display: flex;
    margin-top: 8px;

    @media screen and (max-width: 912px) {
        justify-content: space-between;
        align-items: center;
        width: 100%;
        margin: 0;
    }
`;

function Navbar({ isReauthenticate }) {
    const { navBgColor, linkColor, logo, linkHoverIcon } = useScrollNavbar(isReauthenticate);
    const [isOpen, setIsOpen] = useState(false);

    const toggleNav = () => {
        setIsOpen((open) => !open);
    };

    return (
        <>
            <Container bgColor={navBgColor}>
                <Nav>
                    <NavWrapper>
                        <ImageLink to="/">
                            <Image
                                src={logo}
                                alt="Authenticating.com"
                                width="auto"
                                height="auto"
                                loading="lazy"
                            />
                        </ImageLink>
                        <MenuBar onClick={toggleNav} isOpen={isOpen}>
                            <MenuIcon color={linkColor} />
                        </MenuBar>

                        <NavLinks className="nav_links" isOpen={isOpen}>
                            <ScrollSpyDiv
                                items={[
                                    '#solution',
                                    '#pricing',
                                    '#contact',
                                    'documentation',
                                    'resources',
                                ]}
                                currentClassName="active"
                            >
                                <PageLink
                                    color={linkColor}
                                    linkHoverIcon={linkHoverIcon}
                                    to="/#solution"
                                >
                                    Solutions
                                </PageLink>
                                <SubMenu
                                    linkColor={linkColor}
                                    linkHoverIcon={linkHoverIcon}
                                    name="Products"
                                    data={productsSubMenuItems}
                                />
                                <PageLink
                                    color={linkColor}
                                    linkHoverIcon={linkHoverIcon}
                                    to="/#pricing"
                                >
                                    Pricing
                                </PageLink>
                                <PageLink to="/#contact" mobileOnly>
                                    Contact Us
                                </PageLink>
                                <SubMenu
                                    linkColor={linkColor}
                                    linkHoverIcon={linkHoverIcon}
                                    name="Resources"
                                    data={resourcesSubMenuItems}
                                />
                            </ScrollSpyDiv>
                            <LoginLink
                                href="https://portal.authenticate.com"
                                target="_blank"
                                rel="noreferrer"
                                disable
                            >
                                Log in
                            </LoginLink>
                            <SignInLink>
                                <a
                                    href="https://portal.authenticate.com/register"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Get Started
                                </a>
                            </SignInLink>
                        </NavLinks>
                    </NavWrapper>

                    <NavBtnGroup>
                        <ContactUsCta currentColor={linkColor} href="/#contact">
                            Contact Us
                        </ContactUsCta>
                        <GetStartedCta
                            currentColor={linkColor}
                            href="https://portal.authenticate.com/register"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Get Started
                        </GetStartedCta>
                    </NavBtnGroup>
                </Nav>
            </Container>
            {isOpen && <ResponsiveNavbar setOpen={setIsOpen} />}
        </>
    );
}

Navbar.propTypes = {
    isReauthenticate: PropTypes.bool,
};

Navbar.defaultProps = {
    isReauthenticate: false,
};

export default Navbar;
